import {Button, Chip, Typography} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import {FormApi} from "final-form";
import {useForm} from "react-final-form";
import {ArrayField, Datagrid, DateField, FunctionField, Record, TextField,} from "react-admin";
import {DeliveryField} from "../../components/DeliveryField";
import {EditableCheckboxField} from "../../components/EditableCheckboxField";
import {EditableDatagrid} from "../../components/EditableDatagrid";
import {EditableDateField} from "../../components/EditableDatagrid/EditableDateField";
import {EditableNumberField} from "../../components/EditableDatagrid/EditableNumberField";
import {EditableTextField} from "../../components/EditableDatagrid/EditableTextField";
import {IdentifierField} from "../../components/IdentifierField";
import {SubjectField} from "../../components/SubjectField";
import {DatagridPropsWithOptions} from "../../shared/types";
import {formatEuro, getResidue, initField} from "../../shared/utils";
import {SaveButtonDatagrid} from "./SaveButtonDatagrid";
import {DeliveryTypeField} from "../../components/DeliveryTypeField";

type CarrierPayment = {
    id: number;
    cash_box?: { type?: string } | number | null;
    total?: number | string | null;
    type?: string;
};

const getCarrierPaymentType = (payment: CarrierPayment) =>
    typeof payment.cash_box === "object" && payment.cash_box
        ? payment.cash_box.type || payment.type
        : payment.type;

const CarrierPaymentRow = ({payment}: { payment: CarrierPayment }) => {
    const form = useForm();
    const paymentType = getCarrierPaymentType(payment);
    const source = paymentType === "CO" ? "cash" : paymentType === "AS" ? "check" : null;
    const total = Number(payment.total || 0);

    if (!payment.cash_box || !source) {
        return <Chip label="Non incassato" size="small" color="primary"/>;
    }

    const paymentLabel = source === "cash" ? "Contanti" : "Assegno";

    return (
        <div style={{display: "flex", alignItems: "center", marginBottom: "4px"}}>
            <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<FileCopyIcon fontSize="small"/>}
                disabled={total <= 0}
                aria-label={`Copia ${formatEuro(total)} in ${paymentLabel} e salva`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    form.change(source, total);
                    form.submit();
                }}
            >
                {paymentLabel} {formatEuro(total)}
            </Button>
        </div>
    );
};

export const CachetDatagrid = ({
                                   options,
                                   ...rest
                               }: DatagridPropsWithOptions) => {
    const getInputProps = (source: string, copy: boolean = true) => {
        return (
            record: Record,
            form: FormApi<
                globalThis.Record<string, any>,
                Partial<globalThis.Record<string, any>>
            >
        ) => ({
            style: {minWidth: "11em"},
            endAdornment: copy ? (
                <>
                    <Button
                        style={{minWidth: "auto"}}
                        color="primary"
                        title="Incolla e salva"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.change(source, record.total);
                            form.submit();
                        }}
                    >
                        <FileCopyIcon/>
                    </Button>
                    <SaveButtonDatagrid/>
                </>
            ) : (<><SaveButtonDatagrid/></>),
        });
    };

    return (
        <EditableDatagrid {...rest}>
            <IdentifierField {...initField("id", options)} />
            <EditableCheckboxField
                disabled={(record) => getResidue(record) !== 0}
                {...initField("verified", options)}
            />

            <FunctionField
                label="Incassato"
                render={(record: any) => {
                    const value = getResidue(record) === 0;
                    const color = value ? "green" : "red";
                    return (
                        <div
                            style={{
                                backgroundColor: color,
                                width: "30px",
                                height: "30px",
                                borderRadius: "100%",
                            }}
                        />
                    );
                }}
            />
            <DeliveryField
                deliverySource={"delivery.id"}
                {...initField("delivery", options)}
                noTitle={true}
            />
            <DeliveryTypeField
                source="delivery_type" sortBy="cachet__delivery__delivery_type" options={options} label={"Tipo"}/>
            <TextField
                {...initField("delivery_status_display", options)}>
            </TextField>
            <ArrayField source="delivery.routes" label="Autista/Corriere -&gt; Giro" sortable={false}>
                <Datagrid header={<></>}>
                    <FunctionField
                        render={(record: any) => {
                            return record && record.route ? (
                                <Typography variant="body2">{
                                    record.driver ?
                                        (record.driver.surname + " " + record.driver.name) :
                                        record.carrier
                                } -&gt; {record.route} </Typography>
                            ) : null
                        }
                        }
                    />
                </Datagrid>
            </ArrayField>
            <TextField
                {...initField("delivery.document_number", options)}
                label="N. documento"
                sortBy="delivery__document_number"
            />
            <DateField
                {...initField("delivery.document_date", options)}
                label="Data documento"
                sortBy="delivery__document_date"
            />
            <SubjectField source="delivery.client" label="Cliente" sortBy="delivery__client__business_name"/>
            <SubjectField source="delivery.receiver" label="Destinatario" sortBy="delivery__receiver__business_name"/>
            <TextField {...initField("total", options)} />
            <FunctionField
                label="Corriere"
                render={(record: Record | undefined) => record?.carrier_cachet_payment && (
                    <div>
                        {record.carrier_cachet_payment.map((payment: CarrierPayment) => (
                            <CarrierPaymentRow key={payment.id} payment={payment}/>
                        ))}
                    </div>
                )}
                sortable={false}
            />
            <FunctionField
                label="Residuo"
                render={(record: Record | undefined) => (
                    <>{record ? getResidue(record).toFixed(2) : null}</>
                )}
                sortable={false}
            />
            <EditableNumberField
                alwaysEditable
                getInputProps={getInputProps("cash")}
                {...initField("cash", options)}
                sortable={false}
            />
            <EditableNumberField
                alwaysEditable
                getInputProps={getInputProps("check")}
                {...initField("check", options)}
                sortable={false}
            />
            <EditableNumberField
                alwaysEditable
                getInputProps={getInputProps("rebate")}
                {...initField("rebate", options)}
                sortable={false}
            />
            {/* <EditableNumberField
        alwaysEditable
        getInputProps={getInputProps("credit_card")}
        {...initField("credit_card", options)}
      /> */}
            <EditableTextField
                alwaysEditable
                getInputProps={getInputProps("note", false)}
                {...initField("note", options)}
            />
            <EditableDateField {...initField("collection_date", options)} />
        </EditableDatagrid>
    );
};
